import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import AnimeCard from "@/components/base/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";
import { animeList } from "@/mocks/anime";

const hotKeywords = ["剑鸣回响", "星海彼端", "治愈", "国创", "新番", "美食番"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q")?.trim() ?? "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<"news" | "anime">("anime");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useDocumentMeta(
    keyword ? `搜索「${keyword}」 - h动漫` : "搜索 - h动漫",
    "在h动漫搜索动漫资讯与作品，快速找到你感兴趣的内容。"
  );

  const q = keyword.toLowerCase();

  const matchedNews = useMemo(() => {
    if (!q) return [];
    return newsList.filter(
      (n) =>
        n.title.toLowerCase().includes(q) ||
        n.summary.toLowerCase().includes(q) ||
        n.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [q]);

  const matchedAnime = useMemo(() => {
    if (!q) return [];
    return animeList.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.synopsis.toLowerCase().includes(q) ||
        a.genre.some((g) => g.toLowerCase().includes(q)) ||
        a.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [q]);

  const total = matchedNews.length + matchedAnime.length;
  const list = tab === "news" ? matchedNews : matchedAnime;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = input.trim();
    setParams(v ? { q: v } : {});
    setTab("anime");
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <header className="mt-4 max-w-3xl">
        <h1 className="font-heading font-extrabold text-foreground-950 text-2xl md:text-3xl">搜索</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">
          输入关键词，搜索 h动漫 的资讯与作品，支持标题、类型与标签匹配。
        </p>

        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索动漫资讯或作品，如「剑鸣回响」"
              className="w-full h-11 pl-11 pr-3 rounded-md bg-background-100 border border-background-200 text-base focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300"
            />
          </div>
          <button
            type="submit"
            className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </header>

      {!keyword ? (
        <div className="mt-8">
          <h2 className="font-heading font-semibold text-foreground-950 text-base mb-3">热门关键词</h2>
          <div className="flex flex-wrap gap-2">
            {hotKeywords.map((k) => (
              <Link
                key={k}
                to={`/search?q=${encodeURIComponent(k)}`}
                className="px-3.5 py-2 rounded-full bg-secondary-100 text-secondary-900 text-sm hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {k}
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <EmptyState
              icon="ri-search-eye-line"
              title="开始你的搜索"
              description="试试上方热门关键词，或者输入你感兴趣的动漫名称。"
            />
          </div>
        </div>
      ) : (
        <>
          <p className="mt-6 text-sm text-foreground-700">
            关键词「<span className="font-semibold text-foreground-950">{keyword}</span>」共找到{" "}
            <span className="font-semibold text-primary-600">{total}</span> 条结果
          </p>

          <div className="mt-4 inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
            <button
              type="button"
              onClick={() => setTab("anime")}
              className={`px-4 py-1.5 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap ${
                tab === "anime" ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-700"
              }`}
            >
              动漫作品（{matchedAnime.length}）
            </button>
            <button
              type="button"
              onClick={() => setTab("news")}
              className={`px-4 py-1.5 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap ${
                tab === "news" ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-700"
              }`}
            >
              动漫资讯（{matchedNews.length}）
            </button>
          </div>

          <div className="mt-6">
            {list.length > 0 ? (
              tab === "anime" ? (
                <div data-product-shop className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {matchedAnime.map((a) => (
                    <AnimeCard key={a.id} anime={a} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:gap-4">
                  {matchedNews.map((n) => (
                    <NewsCard key={n.id} news={n} />
                  ))}
                </div>
              )
            ) : (
              <EmptyState
                icon="ri-file-search-line"
                title="未找到相关内容"
                description={`没有与「${keyword}」匹配的${tab === "anime" ? "作品" : "资讯"}，建议修改关键词或切换分类试试。`}
              />
            )}
          </div>

          <div className="mt-8">
            <h2 className="font-heading font-semibold text-foreground-950 text-sm mb-3">换个关键词试试</h2>
            <div className="flex flex-wrap gap-2">
              {hotKeywords.map((k) => (
                <Link
                  key={k}
                  to={`/search?q=${encodeURIComponent(k)}`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-sm hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  {k}
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}